import React, { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { Loader2, Palette, Users, Shield, CheckCircle } from 'lucide-react';

interface PreloaderProps {
  onComplete: () => void;
  minDisplayTime?: number;
  authLoading: boolean;
}

const Preloader: React.FC<PreloaderProps> = ({ onComplete, minDisplayTime = 1000, authLoading }) => {
  const { user, isAdmin } = useAuth();
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [resourcesReady, setResourcesReady] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);

  const steps = [
    { label: 'Initializing', icon: Loader2 },
    { label: 'Securing Connection', icon: Shield },
    { label: 'Loading Assets', icon: Palette },
    { label: 'Ready', icon: CheckCircle }
  ];

  // Get user's display name with proper capitalization
  const getDisplayName = () => {
    if (user?.user_metadata?.first_name && user?.user_metadata?.last_name) {
      const firstName = user.user_metadata.first_name.toLowerCase();
      const lastName = user.user_metadata.last_name.toLowerCase();
      const capitalizedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1);
      const capitalizedLastName = lastName.charAt(0).toUpperCase() + lastName.slice(1);
      return `${capitalizedFirstName} ${capitalizedLastName}`;
    }
    if (user?.user_metadata?.first_name) {
      const firstName = user.user_metadata.first_name.toLowerCase();
      return firstName.charAt(0).toUpperCase() + firstName.slice(1);
    }
    if (user?.email) {
      const emailName = user.email.split('@')[0].toLowerCase();
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }
    return null;
  };

  // Get greeting message based on user status
  const getGreeting = () => {
    const name = getDisplayName();
    
    if (isAdmin && name) {
      return {
        primary: `Welcome back, Admin ${name}`,
        secondary: "Ready to manage your auction empire?",
        icon: Users,
        color: "text-blue-400"
      };
    } else if (user && name) {
      return {
        primary: `Welcome back, ${name}`,
        secondary: "Ready to discover amazing artworks?",
        icon: Users,
        color: "text-emerald-400"
      };
    } else {
      return {
        primary: "Florida Highwaymen Auction",
        secondary: "Discover extraordinary art and timeless treasures",
        icon: Palette,
        color: "text-teal-400"
      };
    }
  };

  const greeting = getGreeting();

  // Resource Detection: Wait for fonts and critical assets with timeout
  useEffect(() => {
    const RESOURCE_TIMEOUT = 2000; // Increased to 2 seconds for smoother experience
    let timeoutId: NodeJS.Timeout;

    const checkResources = async () => {
      try {
        // Create a timeout promise
        const timeoutPromise = new Promise((resolve) => {
          timeoutId = setTimeout(() => {
            console.log('Resource loading timeout reached, continuing...');
            resolve('timeout');
          }, RESOURCE_TIMEOUT);
        });

        // Wait for fonts with timeout
        const fontPromise = (async () => {
          if (document.fonts && document.fonts.ready) {
            await document.fonts.ready;
          }
          return 'fonts-ready';
        })();

        // Race between actual loading and timeout
        await Promise.race([
          Promise.all([fontPromise]),
          timeoutPromise
        ]);

        // Always mark as ready after timeout or completion
        setFontsReady(true);
        setResourcesReady(true);
      } catch (error) {
        console.warn('Resource loading check failed, continuing anyway:', error);
        setResourcesReady(true);
        setFontsReady(true);
      }
    };

    checkResources();

    // Cleanup timeout on unmount
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  // Maximum timeout safeguard - ensure page always loads
  useEffect(() => {
    const MAX_PRELOADER_TIME = 4000; // Increased to 4 seconds for more graceful experience
    const maxTimer = setTimeout(() => {
      console.log('Max preloader timeout reached, forcing completion');
      setResourcesReady(true);
      setIsComplete(true);
    }, MAX_PRELOADER_TIME);

    return () => clearTimeout(maxTimer);
  }, []);

  // Coordinated loading sequence with smoother, more natural progression
  useEffect(() => {
    let progressInterval: NodeJS.Timeout;
    let stepInterval: NodeJS.Timeout;

    // Smooth, natural progress animation
    progressInterval = setInterval(() => {
      setProgress(prev => {
        // Don't complete progress until resources AND auth are ready
        const maxProgress = (resourcesReady && !authLoading) ? 100 : 92;
        
        if (prev >= maxProgress) {
          if (maxProgress === 100) {
            setIsComplete(true);
          }
          return maxProgress;
        }
        
        // Natural deceleration curve - faster at start, slower near end
        const remainingProgress = maxProgress - prev;
        const increment = Math.max(2, remainingProgress * 0.08 + Math.random() * 3);
        
        return Math.min(prev + increment, maxProgress);
      });
    }, 100); // Slower, smoother ticks (was 60ms)

    // Slower, more deliberate step progression
    stepInterval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev < steps.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 800); // Much slower steps for better readability (was 200ms)

    return () => {
      clearInterval(progressInterval);
      clearInterval(stepInterval);
    };
  }, [resourcesReady, authLoading]);

  // Handle completion only when everything is ready
  useEffect(() => {
    // Must wait for: resources ready + auth finished + visual completion
    if (resourcesReady && !authLoading && isComplete) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onComplete, 200); // Slightly longer for smoothness
      }, 400); // Longer pause to appreciate completion state

      return () => clearTimeout(timer);
    }
  }, [resourcesReady, authLoading, isComplete, onComplete, minDisplayTime]);

  if (!isVisible) {
    return null;
  }

  const CurrentIcon = steps[currentStep]?.icon || Loader2;

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-emerald-950 via-teal-900 to-blue-950 overflow-hidden">
      {/* Animated wave background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute bottom-0 left-0 right-0 h-96 bg-gradient-to-t from-emerald-500/20 via-teal-500/10 to-transparent animate-wave" style={{ animationDelay: '0s' }} />
          <div className="absolute bottom-0 left-0 right-0 h-96 bg-gradient-to-t from-blue-500/15 via-cyan-500/10 to-transparent animate-wave" style={{ animationDelay: '1s' }} />
        </div>
        
        {/* Floating particles */}
        <div className="absolute inset-0">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white/40 rounded-full animate-float-particle"
              style={{
                left: `${(i * 8.33) + Math.random() * 8}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: `${8 + Math.random() * 4}s`
              }}
            />
          ))}
        </div>

        {/* Radial gradient lights */}
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-emerald-400/10 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-1/4 right-1/3 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
      </div>

      {/* Main centered card */}
      <div className="flex items-center justify-center min-h-screen px-4 py-8 relative">
        <div className="relative max-w-md w-full">
          {/* Glass morphism card */}
          <div className="relative bg-white/5 backdrop-blur-2xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden p-8 md:p-10 animate-in fade-in zoom-in duration-1000 ease-out">
            {/* Card glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-blue-500/5 pointer-events-none transition-opacity duration-1000" />
            
            {/* Content */}
            <div className="relative z-10">
              {/* Circular progress with logo */}
              <div className="flex justify-center mb-8">
                <div className="relative w-40 h-40">
                  {/* Circular progress track */}
                  <svg className="w-40 h-40 transform -rotate-90">
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth="4"
                      fill="none"
                    />
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      stroke="url(#progressGradient)"
                      strokeWidth="4"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 70}`}
                      strokeDashoffset={`${2 * Math.PI * 70 * (1 - progress / 100)}`}
                      className="transition-all duration-700 ease-in-out"
                      style={{ transition: 'stroke-dashoffset 0.7s cubic-bezier(0.4, 0, 0.2, 1)' }}
                    />
                    <defs>
                      <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={isComplete ? "#34d399" : "#60a5fa"} />
                        <stop offset="50%" stopColor={isComplete ? "#14b8a6" : "#06b6d4"} />
                        <stop offset="100%" stopColor={isComplete ? "#0d9488" : "#14b8a6"} />
                      </linearGradient>
                    </defs>
                  </svg>
                  
                  {/* Center logo */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative">
                      <div className={`w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 via-teal-500 to-blue-600 flex items-center justify-center shadow-2xl transition-all duration-700 ease-out ${isComplete ? 'scale-110' : 'scale-100'}`}>
                        <span className="text-white font-light text-3xl transition-transform duration-500">F</span>
                      </div>
                      
                      {/* Animated icon indicator */}
                      <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-white/10 backdrop-blur-xl rounded-full border border-white/20 flex items-center justify-center transition-all duration-500">
                        <CurrentIcon className={`h-5 w-5 transition-all duration-300 ${isComplete ? 'text-emerald-300' : 'text-blue-300'} ${!isComplete ? 'animate-spin' : ''}`} />
                      </div>
                      
                      {/* Success checkmark */}
                      {isComplete && (
                        <div className="absolute inset-0 flex items-center justify-center animate-in zoom-in duration-700 ease-out">
                          <CheckCircle className="h-8 w-8 text-emerald-300 absolute drop-shadow-lg" />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Progress percentage */}
                  <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2">
                    <div className={`px-4 py-1.5 rounded-full backdrop-blur-xl border text-sm font-medium transition-all duration-700 ease-out ${
                      isComplete
                        ? 'bg-emerald-500/20 border-emerald-400/30 text-emerald-200 scale-105'
                        : 'bg-blue-500/20 border-blue-400/30 text-blue-200 scale-100'
                    }`}>
                      {Math.round(progress)}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Title and branding */}
              <div className="text-center mb-6 mt-8">
                <h1 className="text-2xl md:text-3xl font-light mb-2 bg-gradient-to-r from-emerald-200 via-teal-200 to-blue-200 bg-clip-text text-transparent">
                  Florida Highwaymen
                </h1>
                <p className="text-base text-white/60 font-light tracking-wide">Auction House</p>
              </div>

              {/* Greeting section */}
              <div className="text-center mb-8 space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-700 delay-300">
                <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 transition-all duration-500 hover:bg-white/10">
                  <greeting.icon className={`h-4 w-4 ${greeting.color} transition-colors duration-500`} />
                  <p className="text-sm text-white/90 font-light transition-all duration-300">{greeting.primary}</p>
                </div>
                <p className="text-xs text-white/50 max-w-xs mx-auto font-light transition-all duration-500">
                  {greeting.secondary}
                </p>
              </div>

              {/* Current status */}
              <div className="text-center mb-6">
                <p className="text-sm font-light text-white/70 tracking-wide transition-all duration-500 ease-in-out">
                  {steps[currentStep]?.label || 'Loading'}
                </p>
              </div>

              {/* Step indicators - modern dots */}
              <div className="flex justify-center space-x-2">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className="relative group"
                  >
                    <div
                      className={`w-10 h-1.5 rounded-full transition-all duration-700 ease-in-out ${
                        index <= currentStep
                          ? (isComplete 
                              ? 'bg-gradient-to-r from-emerald-400 to-teal-400 shadow-lg shadow-emerald-400/40 scale-105' 
                              : 'bg-gradient-to-r from-blue-400 to-cyan-400 shadow-lg shadow-blue-400/40 scale-105')
                          : 'bg-white/20 scale-100'
                      }`}
                      style={{
                        transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
                      }}
                    />
                    {/* Tooltip on hover */}
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 ease-out pointer-events-none">
                      <div className="bg-black/90 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap shadow-xl">
                        {step.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer text */}
          <div className="text-center mt-6 animate-in fade-in duration-1000 delay-700">
            <p className="text-xs text-white/40 font-light tracking-wider transition-all duration-500">
              {isComplete ? 'Experience ready' : 'Preparing your experience...'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Preloader;
